import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FaExclamationTriangle } from "react-icons/fa";
import axios, { AxiosResponse } from "axios";
import openNotification from "../utils/CustomNotification";
import { truncate } from "fs";

const beaddress = `${process.env.REACT_APP_API_URL}/api/verify-2fa-otp-code`;

interface LocationState {
  email?: string;
  showRecovery?: boolean;
}

const getHeaders = () => ({
  accept: "application/json",
  "user-uid": localStorage.getItem("user_uid"),
});

const AuthenticatorCodeEntry: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, showRecovery } = (location.state as LocationState) || {};

  const [code, setCode] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    sessionStorage.setItem("showRecovery", showRecovery ? "true" : "false");
  }, [showRecovery]);

  const handleCancel = () => {
    navigate(-1);
  };

  interface VerifyResponse {
    status: string;
    message?: string;
  }

  const handleSubmit = async (): Promise<void> => {
    if (!code || code.length < 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    try {
      const response: AxiosResponse<VerifyResponse> = await axios.post(
        beaddress,
        code,
        { headers: getHeaders(), withCredentials: true },
      );

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message: "Authenticator code verified successfully!",
        });
        navigate("/recovery-code-display");
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Invalid code. Try again.",
        });
      }
    } catch (err: unknown) {
      openNotification({
        type: "error",
        message: "Verification failed. Please try again.",
      });
    }
  };

  return (
    <StyledWrapper>
      <Container>
        <Title>Enter Authenticator Code</Title>

        <Label>Authenticator Code</Label>
        <Input
          type="number"
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCode(e.target.value);
            setError("");
          }}
          placeholder="Enter 6-digit code"
        />

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <TwofaNoteBlock>
          <NoteIcon />
          <span>This code is generated by your authenticator app.</span>
        </TwofaNoteBlock>

        <ButtonGroup>
          <CancelBtn onClick={handleCancel}>Cancel</CancelBtn>
          <SubmitBtn onClick={handleSubmit}>Submit</SubmitBtn>
        </ButtonGroup>
      </Container>
    </StyledWrapper>
  );
};

export default AuthenticatorCodeEntry;

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  /* background-color: #f4f4f4; */
`;

const Container = styled.div`
  padding: 1.5rem;
  max-width: 30rem;
  width: 100%;
  border: 0.1rem solid #ccc;
  border-radius: 0.75rem;
  background-color: #fff;
  box-shadow: 0 0 0.625rem rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.375rem;
  color: #444;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 0.1rem solid #ccc;
  border-radius: 0.5rem;
  width: 100%;
  background-color: #f3f3f3;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 1rem;
  box-sizing: border-box;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: bold;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
`;

const CancelBtn = styled(Button)`
  background-color: red;
  border: 1px solid black;
  color: white;
  width: 48%;
`;

const SubmitBtn = styled(Button)`
  background-color: #094752;
  color: white;
  width: 48%;
`;

const TwofaNoteBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 1rem;
  font-size: 0.9rem;
  color: #555;
  flex-wrap: wrap;
  border: 0.1rem solid #ccc;
  border-radius: 0.5rem;
  background-color: #fef8e7;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const NoteIcon = styled(FaExclamationTriangle)`
  color: #e6a000;
  font-size: 1.2rem;
  margin-right: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
`;

const ErrorMsg = styled.div`
  color: red;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;
